import type { CalendarEvent } from "./api";

export interface EventSegment {
  event: CalendarEvent;
  lane: number;
  isStart: boolean;
  isEnd: boolean;
  showLabel: boolean;
  labelSpan: number;
}

export type WeekLayout = Map<string, (EventSegment | null)[]>;

function compareForLayout(a: CalendarEvent, b: CalendarEvent): number {
  if (a.startDate !== b.startDate) return a.startDate.localeCompare(b.startDate);
  if (a.endDate !== b.endDate) return b.endDate.localeCompare(a.endDate);
  if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
  const time = (a.startTime ?? "").localeCompare(b.startTime ?? "");
  if (time !== 0) return time;
  return a.title.localeCompare(b.title);
}

export function layoutWeek(
  weekDates: string[],
  events: CalendarEvent[]
): WeekLayout {
  const layout: WeekLayout = new Map(weekDates.map((date) => [date, []]));
  if (weekDates.length === 0) return layout;
  const weekStart = weekDates[0];
  const weekEnd = weekDates[weekDates.length - 1];
  const laneLastIndex: number[] = [];

  events
    .filter((event) => event.startDate <= weekEnd && event.endDate >= weekStart)
    .sort(compareForLayout)
    .forEach((event) => {
      const from =
        event.startDate < weekStart ? 0 : weekDates.indexOf(event.startDate);
      const to =
        event.endDate > weekEnd
          ? weekDates.length - 1
          : weekDates.indexOf(event.endDate);
      if (from < 0 || to < from) return;

      let lane = laneLastIndex.findIndex((last) => last < from);
      if (lane === -1) lane = laneLastIndex.length;
      laneLastIndex[lane] = to;

      for (let i = from; i <= to; i++) {
        const date = weekDates[i];
        const slots = layout.get(date)!;
        while (slots.length <= lane) slots.push(null);
        slots[lane] = {
          event,
          lane,
          isStart: date === event.startDate,
          isEnd: date === event.endDate,
          showLabel: i === from,
          labelSpan: to - from + 1,
        };
      }
    });

  return layout;
}
